import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL, API_CLIENT_ID } from './apiConfig';
import { clearAuthSession } from '../features/auth/session';

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    // >= LLM_CLIENT_TIMEOUT_SECONDS (backend, 120s) + margine: la synthesis con
    // ~50k token di contesto è legittimamente lenta (30-60s tipico, fino a
    // ~120s nei casi peggiori) — un timeout più aggressivo (es. 30s) taglierebbe
    // richieste valide. Oltre il timeout: errore GESTITO (niente spinner
    // infinito), non uno sblocco della richiesta lato server (piano Fase 1 §2.5).
    timeout: 130000,
    headers: {
        'Content-Type': 'application/json',
    },
});

let isRefreshing = false;
let refreshPromise = null;

// Check if ACCESS token is expired (with 30 second buffer)
const isTokenExpired = (token) => {
    if (!token) return true;

    try {
        const decoded = jwtDecode(token);
        return decoded.exp < (Date.now() / 1000 + 30);
    } catch (error) {
        return true;
    }
};

// Refresh token and update storage
const refreshAccessToken = async () => {
    const refreshToken = sessionStorage.getItem('refresh_token');

    if (!refreshToken) {
        clearAuthSession();
        window.location.href = '/login';
        return null;
    }

    try {
        const response = await axios.post(`${API_BASE_URL}/oauth/refresh`, {
            refresh_token: refreshToken,
            client_id: API_CLIENT_ID,
            grant_type: 'refresh_token',
        });

        const { access_token, refresh_token: newRefreshToken } = response.data;

        sessionStorage.setItem('access_token', access_token);
        if (newRefreshToken) {
            sessionStorage.setItem('refresh_token', newRefreshToken);
        }

        console.log('✅ Token refreshed');
        return access_token;
    } catch (error) {
        console.error('❌ Token refresh failed');
        clearAuthSession();
        window.location.href = '/login';
        return null;
    }
};

// Garantisce un access_token valido (refresh se scaduto o mancante), riusata
// sia dall'interceptor axios sia dal consumer fetch/SSE di /query/stream
// (Fase 2, piano §3.4) — fetch NON passa dagli interceptor axios, quindi la
// logica di refresh va estratta qui invece che duplicata. Ritorna il token
// valido, oppure null se non è stato possibile ottenerne uno (sessione
// invalida: clearAuthSession() + redirect a /login sono già stati eseguiti).
export const ensureFreshToken = async () => {
    let accessToken = sessionStorage.getItem('access_token');
    const refreshToken = sessionStorage.getItem('refresh_token');

    // If we have an access token but NO refresh token, something is wrong
    if (accessToken && !refreshToken) {
        console.warn('⚠️ Access token exists but no refresh token - forcing logout');
        clearAuthSession();
        window.location.href = '/login';
        return null;
    }

    // If access token expired, refresh it
    if (isTokenExpired(accessToken)) {
        console.log('⚠️ Access token expired, refreshing...');

        if (!isRefreshing) {
            isRefreshing = true;
            refreshPromise = refreshAccessToken().finally(() => {
                isRefreshing = false;
                refreshPromise = null;
            });
        }

        accessToken = await refreshPromise;
    }

    return accessToken;
};

// Request interceptor - Add Bearer token and handle expiration
axiosInstance.interceptors.request.use(
    async (config) => {
        const hadAccessTokenWithoutRefresh =
            sessionStorage.getItem('access_token') && !sessionStorage.getItem('refresh_token');

        const accessToken = await ensureFreshToken();

        if (!accessToken && hadAccessTokenWithoutRefresh) {
            return Promise.reject(new axios.Cancel('No refresh token available'));
        }

        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor - Handle 401 errors only
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        // Handle cancelled requests silently
        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }

        const originalRequest = error.config;

        // If 401 and haven't retried yet, try to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            if (!isRefreshing) {
                isRefreshing = true;
                refreshPromise = refreshAccessToken().finally(() => {
                    isRefreshing = false;
                    refreshPromise = null;
                });
            }

            const accessToken = await refreshPromise;

            // If refresh failed, reject
            if (!accessToken) {
                return Promise.reject(error);
            }

            // Retry the original request
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return axiosInstance(originalRequest);
        }

        // Let all other errors bubble up to components
        return Promise.reject(error);
    }
);

export default axiosInstance;